/**
 * WhatTail — the questionnaire a bartender runs with a guest to land on a
 * drink (Rob, 2026-10-12).
 *
 * A handful of closed questions — what they like the smell and taste of, how
 * strong, how it is served, which spirits — are scored against every drink
 * on tonight's list, and three come back, numbered.
 *
 * Two rules are worth naming.
 *
 * **No language model, anywhere.** A bartender mid-service wants an answer
 * the instant the last question is tapped, the same answer every time, and
 * one that never costs the bar a cent. Everything here is arithmetic over the
 * library and the flavour database's profiles (D16), pure and deterministic.
 *
 * **Fit decides who is on the shortlist; gross profit decides the order.**
 * The three suggestions are drinks that fit what the guest asked for, and of
 * those the one the bar makes most on is first. The margin is never shown —
 * the bartender sees 1, 2, 3 and why each one suits, and that is all. A
 * drink that does not fit is never suggested for its margin: only drinks
 * within `FIT_BAND` of the best fit are in the running.
 */
import { lineAromaWeight, topAromaFamilies } from "./aroma.js";
import { grossProfit } from "./calc.js";
import { FLAVOUR_FAMILY_KEYS, FLAVOUR_GUEST_LABELS, FLAVOUR_TASTES, toFlavourFamily, } from "./flavour.js";
import { specTotals } from "./library.js";
import { isOnMenu } from "./menu.js";
import { expandBatches } from "./recipes.js";
// ---------------------------------------------------------------- vocabulary
/** The tastes a guest is asked about. The flavour database also scores salty and umami; nobody orders by them. */
export const WHATTAIL_TASTES = ["Sweet", "Sour", "Bitter"];
export const WHATTAIL_STRENGTHS = ["light", "medium", "strong"];
export const STRENGTH_LABEL = {
    light: "Light",
    medium: "Medium",
    strong: "Strong",
};
/**
 * Where light ends and strong begins, percent ABV of the finished drink. A
 * highball sits around 8–12%, a sour around 13–20%, a stirred drink on ice in
 * the high twenties and up.
 */
export const STRENGTH_MEDIUM_FROM = 13;
export const STRENGTH_STRONG_FROM = 23;
export function strengthOf(abv) {
    return abv >= STRENGTH_STRONG_FROM ? "strong" : abv >= STRENGTH_MEDIUM_FROM ? "medium" : "light";
}
export const WHATTAIL_SERVES = ["long", "rocks", "up"];
export const SERVE_LABEL = {
    long: "Long and tall",
    rocks: "Short, on ice",
    up: "Up, no ice",
};
/**
 * How a drink is served, from what the spec says: its tags first (the bar's
 * own words), then its glass. Null when neither says.
 */
export function serveOf(spec) {
    const tags = spec.tags.map((t) => t.trim().toLowerCase());
    if (tags.some((t) => t === "served long" || t === "long" || t === "highball"))
        return "long";
    if (tags.some((t) => t === "on the rocks" || t === "rocks"))
        return "rocks";
    if (tags.some((t) => t === "up" || t === "neat" || t === "straight up"))
        return "up";
    const glass = spec.glass.trim().toLowerCase();
    if (/highball|collins|tall|hurricane|pint/.test(glass))
        return "long";
    if (/rocks|old fashioned|tumbler/.test(glass))
        return "rocks";
    if (/coupe|martini|nick|flute/.test(glass))
        return "up";
    return null;
}
/** The spirits a guest can ask for or rule out. */
export const WHATTAIL_SPIRITS = ["gin", "vodka", "rum", "whisky", "agave", "brandy"];
export const SPIRIT_LABEL = {
    gin: "Gin",
    vodka: "Vodka",
    rum: "Rum",
    whisky: "Whisky",
    agave: "Tequila and mezcal",
    brandy: "Brandy",
};
/**
 * The words that give a bottle's spirit away, in its own name or the flavour
 * database name it is linked to. The library has no "spirit type" field, so
 * this reads the label the way a person would: the category word first, and
 * the handful of brands a back bar carries that do not print one.
 */
const SPIRIT_WORDS = {
    gin: /\b(gin|genever|jenever)\b/,
    vodka: /\b(vodka|grey goose|ketel one|belvedere|absolut)\b/,
    rum: /\b(rum|rhum|ron|cachaca|cachaça|white cane|plantation|planteray|bacardi|havana club|appleton|diplomatico|goslings)\b/,
    whisky: /\b(whisky|whiskey|bourbon|rye|scotch|malt|islay|laphroaig|johnnie walker|monkey shoulder|nikka|jim beam|michters|mitchers|makers mark|buffalo trace|bulleit|jameson|talisker|ardbeg|lagavulin|glenfiddich)\b/,
    agave: /\b(tequila|tequilana|mezcal|agave|espadin|raicilla|sotol)\b/,
    brandy: /\b(brandy|cognac|armagnac|calvados|pisco|grappa|eau de vie)\b/,
};
/** The spirit a bottle is, or null for anything that is not one of the six. */
export function spiritOf(ing) {
    if (ing.kind !== "spirit")
        return null;
    const text = `${ing.name} ${ing.flavour?.ref ?? ""}`.toLowerCase().replace(/[’']/g, "");
    // "Ginger" is not gin and "Rye" bread is not here: `\b` and `kind` see to both.
    return WHATTAIL_SPIRITS.find((s) => SPIRIT_WORDS[s].test(text)) ?? null;
}
const TASTE_INDEX = {
    Sweet: FLAVOUR_TASTES.indexOf("Sweet"),
    Sour: FLAVOUR_TASTES.indexOf("Sour"),
    Bitter: FLAVOUR_TASTES.indexOf("Bitter"),
};
/**
 * The millilitres of each spirit in a build, following a house batch down
 * into its own build (a batch is a spec whose id is the ingredient's).
 */
function spiritMl(spec, ingredientsById, specsById, scale = 1, seen = new Set(), into = new Map()) {
    for (const line of spec.lines) {
        const ing = ingredientsById.get(line.ingredientId);
        if (!ing || !(line.ml > 0))
            continue;
        const spirit = spiritOf(ing);
        if (spirit) {
            into.set(spirit, (into.get(spirit) ?? 0) + line.ml * scale);
            continue;
        }
        const batch = specsById.get(line.ingredientId);
        if (!batch || seen.has(line.ingredientId))
            continue;
        const batchMl = batch.lines.reduce((t, l) => t + (l.ml > 0 ? l.ml : 0), 0);
        if (batchMl <= 0)
            continue;
        spiritMl(batch, ingredientsById, specsById, (scale * line.ml) / batchMl, new Set(seen).add(line.ingredientId), into);
    }
    return into;
}
/**
 * Every drink on tonight's list — listed by the organisation, not 86'd by the
 * venue, and priced — profiled once so the questions can be answered against
 * it instantly. An unpriced drink is left out: it has no margin to order on.
 */
export function whatTailMenu(input) {
    const specsById = new Map(input.specs.map((s) => [s.specId, s]));
    const profiles = input.profilesByIngredientId;
    const raw = input.specs.flatMap((spec) => {
        const override = input.overridesBySpecId.get(spec.specId);
        const price = override?.price ?? null;
        if (!isOnMenu(spec, override) || price === null || price <= 0)
            return [];
        const totals = specTotals(spec, input.ingredientsById);
        // Aroma and taste, line by line, weighted the way the wheel weights
        // them — from scratch, with every batch opened into what it is made of,
        // so a drink that pours a batch tastes of the batch's bottles.
        const familySums = {};
        const tasteSums = { Sweet: 0, Sour: 0, Bitter: 0 };
        let familyWeight = 0;
        let tasteWeight = 0;
        let profiled = false;
        for (const line of expandBatches(spec.lines, input.ingredientsById)) {
            const ing = input.ingredientsById.get(line.ingredientId);
            if (!ing || ing.kind === "dilution")
                continue;
            const weight = lineAromaWeight({ ...line, unit: "ml" });
            if (weight === 0)
                continue;
            const profile = profiles?.get(line.ingredientId);
            const scores = profile ? FLAVOUR_FAMILY_KEYS.map((_, i) => Math.max(0, profile.families[i] ?? 0)) : null;
            const scoreTotal = scores?.reduce((t, s) => t + s, 0) ?? 0;
            if (scores && scoreTotal > 0) {
                FLAVOUR_FAMILY_KEYS.forEach((family, i) => {
                    if (scores[i] > 0)
                        familySums[family] = (familySums[family] ?? 0) + (weight * scores[i]) / scoreTotal;
                });
                familyWeight += weight;
            }
            else {
                const family = toFlavourFamily(ing.aromaFamily);
                if (family) {
                    familySums[family] = (familySums[family] ?? 0) + weight;
                    familyWeight += weight;
                }
            }
            // Every line is in the denominator: a bottle with no profile adds
            // volume and no known taste, which is what it does to the drink too.
            tasteWeight += weight;
            if (profile) {
                for (const taste of WHATTAIL_TASTES)
                    tasteSums[taste] += weight * Math.max(0, profile.tastes[TASTE_INDEX[taste]] ?? 0);
                profiled = true;
            }
        }
        const spirits = [...spiritMl(spec, input.ingredientsById, specsById)];
        const spiritTotal = spirits.reduce((t, [, ml]) => t + ml, 0);
        return [
            {
                spec,
                price,
                gp: grossProfit(price, totals.cost),
                costed: totals.cost > 0,
                abv: totals.abv,
                families: Object.fromEntries(Object.entries(familySums).map(([family, sum]) => [family, familyWeight > 0 ? sum / familyWeight : 0])),
                rawTastes: profiled && tasteWeight > 0
                    ? { Sweet: tasteSums.Sweet / tasteWeight, Sour: tasteSums.Sour / tasteWeight, Bitter: tasteSums.Bitter / tasteWeight }
                    : null,
                spirits: spirits
                    .map(([spirit, ml]) => ({ spirit, share: spiritTotal > 0 ? ml / spiritTotal : 0 }))
                    .sort((a, b) => b.share - a.share || WHATTAIL_SPIRITS.indexOf(a.spirit) - WHATTAIL_SPIRITS.indexOf(b.spirit)),
            },
        ];
    });
    // "Sweet" means sweet for this list: the sweetest drink on it is 1.
    const top = { Sweet: 0, Sour: 0, Bitter: 0 };
    for (const r of raw) {
        if (!r.rawTastes)
            continue;
        for (const taste of WHATTAIL_TASTES)
            top[taste] = Math.max(top[taste], r.rawTastes[taste]);
    }
    return raw.map((r) => ({
        specId: r.spec.specId,
        name: r.spec.name,
        price: r.price,
        gp: r.gp,
        costed: r.costed,
        abv: r.abv,
        strength: strengthOf(r.abv),
        serve: serveOf(r.spec),
        glass: r.spec.glass,
        spirits: r.spirits,
        families: r.families,
        tastes: r.rawTastes
            ? {
                Sweet: top.Sweet > 0 ? r.rawTastes.Sweet / top.Sweet : 0,
                Sour: top.Sour > 0 ? r.rawTastes.Sour / top.Sour : 0,
                Bitter: top.Bitter > 0 ? r.rawTastes.Bitter / top.Bitter : 0,
            }
            : null,
    }));
}
export function whatTailOptions(drinks) {
    return {
        spirits: WHATTAIL_SPIRITS.filter((s) => drinks.some((d) => d.spirits.some((x) => x.spirit === s))),
        serves: WHATTAIL_SERVES.filter((s) => drinks.some((d) => d.serve === s)),
        strengths: WHATTAIL_STRENGTHS.filter((s) => drinks.some((d) => d.strength === s)),
        families: FLAVOUR_FAMILY_KEYS.filter((f) => drinks.some((d) => (d.families[f] ?? 0) >= MIN_FAMILY_SHARE)),
        tastes: drinks.some((d) => d.tastes !== null),
    };
}
export const NO_ANSWERS = { families: [], tastes: [], strength: null, serve: null, spirits: [], avoid: [] };
/** A family has to be at least this much of a drink's aroma to be worth saying. */
const MIN_FAMILY_SHARE = 0.08;
/**
 * How much each answered question counts. Flavour leads — it is the question
 * with the most to say — and the rest are near enough equal. A question the
 * guest skipped does not count at all, for or against.
 */
export const WHATTAIL_WEIGHTS = { families: 3, tastes: 2, spirits: 2, strength: 1.5, serve: 1.5 };
/**
 * A drink is in the running when it fits at least this well against the best
 * fit on the list. Inside the band, margin orders them; outside it, no
 * margin is enough.
 */
export const FIT_BAND = 0.8;
/** How many suggestions come back. */
export const WHATTAIL_SUGGESTIONS = 3;
/** The margin to order on: a drink with no cost entered has none to speak of. */
const margin = (d) => (d.costed ? d.gp : -Infinity);
const byGp = (a, b) => {
    const [x, y] = [margin(a), margin(b)];
    return x === y ? 0 : y > x ? 1 : -1;
};
/** A strength one step away is half right; two steps is wrong. */
function strengthScore(wanted, got) {
    const gap = Math.abs(WHATTAIL_STRENGTHS.indexOf(wanted) - WHATTAIL_STRENGTHS.indexOf(got));
    return gap === 0 ? 1 : gap === 1 ? 0.5 : 0;
}
function score(drink, answers) {
    let total = 0;
    let weight = 0;
    const reasons = [];
    const add = (w, value) => {
        total += w * value;
        weight += w;
    };
    const wantedFamilies = new Set(answers.families.map(toFlavourFamily).filter((f) => f !== null));
    if (wantedFamilies.size > 0) {
        const matched = topAromaFamilies(drink.families, FLAVOUR_FAMILY_KEYS.length).filter((f) => wantedFamilies.has(f.family));
        add(WHATTAIL_WEIGHTS.families, Math.min(1, matched.reduce((t, f) => t + f.share, 0)));
        const said = matched.filter((f) => f.share >= MIN_FAMILY_SHARE).map((f) => FLAVOUR_GUEST_LABELS[f.family]);
        if (said.length > 0)
            reasons.push(said.join(" and "));
    }
    if (answers.tastes.length > 0) {
        const tastes = drink.tastes;
        const value = tastes ? answers.tastes.reduce((t, taste) => t + tastes[taste], 0) / answers.tastes.length : 0;
        add(WHATTAIL_WEIGHTS.tastes, value);
        const said = tastes ? answers.tastes.filter((taste) => tastes[taste] >= 0.5) : [];
        if (said.length > 0)
            reasons.push(said.join(" and "));
    }
    if (answers.spirits.length > 0) {
        const share = drink.spirits.filter((s) => answers.spirits.includes(s.spirit));
        add(WHATTAIL_WEIGHTS.spirits, Math.min(1, share.reduce((t, s) => t + s.share, 0)));
        if (share.length > 0)
            reasons.push(share.map((s) => SPIRIT_LABEL[s.spirit]).join(" and "));
    }
    if (answers.strength) {
        const value = strengthScore(answers.strength, drink.strength);
        add(WHATTAIL_WEIGHTS.strength, value);
        if (value === 1)
            reasons.push(STRENGTH_LABEL[drink.strength]);
    }
    if (answers.serve) {
        const hit = drink.serve === answers.serve;
        add(WHATTAIL_WEIGHTS.serve, hit ? 1 : 0);
        if (hit)
            reasons.push(SERVE_LABEL[answers.serve]);
    }
    return { drink, fit: weight > 0 ? total / weight : 1, reasons };
}
/**
 * The three drinks to offer, in the order to offer them.
 *
 * 1. A drink with a spirit the guest will not drink is out, whatever else.
 * 2. Every drink left is scored on the questions that were answered.
 * 3. The shortlist is every drink within `FIT_BAND` of the best fit, and its
 *    three highest gross profits come back, highest first. A tie goes to the
 *    better fit, then the name, so the order never flickers.
 * 4. When the shortlist is fewer than three, the next-best fits follow it,
 *    in order of fit — after the drinks that fit, never ahead of one for
 *    their margin. A drink that fits nothing at all is never offered.
 */
export function whatTailSuggest(drinks, answers, count = WHATTAIL_SUGGESTIONS) {
    const allowed = drinks.filter((d) => !d.spirits.some((s) => answers.avoid.includes(s.spirit)));
    const byFit = allowed
        .map((d) => score(d, answers))
        .filter((s) => s.fit > 0)
        .sort((a, b) => b.fit - a.fit || byGp(a.drink, b.drink) || a.drink.name.localeCompare(b.drink.name));
    const best = byFit[0]?.fit ?? 0;
    const inBand = byFit.filter((s) => s.fit >= best * FIT_BAND - 1e-9);
    const byMargin = [...inBand].sort((a, b) => byGp(a.drink, b.drink) || b.fit - a.fit || a.drink.name.localeCompare(b.drink.name));
    const suggestions = [...byMargin, ...byFit.slice(inBand.length)]
        .slice(0, Math.max(0, count))
        .map((s, i) => ({ rank: i + 1, drink: s.drink, fit: s.fit, reasons: s.reasons }));
    return { suggestions, considered: allowed.length, shortlisted: inBand.length };
}
//# sourceMappingURL=whattail.js.map